import { TBoardContextType } from "../interfaces/boardContext";

export const BOARD_CONTEXT_TYPE_STORAGE_KEY = "board_context_type";
export const BOARD_CONTEXT_ID_STORAGE_KEY = "board_context_id";
export const BOARD_SELECTION_STORAGE_KEY = "board_selection";
export const BOARD_PENDING_QUERY_SELECTION_STORAGE_KEY = "board_pending_query_selection";
export const LEGACY_SITE_STORAGE_KEY = "siteid";

export const SITE_TYPENAME = "illumina_sitelocation";
export const REGION_TYPENAME = "illumina_region";

export interface IBoardSelectionInput {
    selectionMode: TBoardContextType;
    contextId: string;
    selectedSiteIds?: string[];
}

export type IBoardSelectionStored = IBoardSelectionInput;

export const normalizeContextType = (typename: string | null | undefined): TBoardContextType => {
    return String(typename ?? "").trim().toLowerCase() === REGION_TYPENAME ? "region" : "site";
};

export const normalizeSiteIdList = (siteIds: Array<string | null | undefined>): string[] => {
    const seen = new Set<string>();
    const normalized: string[] = [];

    siteIds.forEach((siteId) => {
        const value = String(siteId ?? "").trim();
        if (!value || seen.has(value)) {
            return;
        }

        seen.add(value);
        normalized.push(value);
    });

    return normalized;
};

export const sortSiteIds = (siteIds: string[]): string[] => {
    return [...normalizeSiteIdList(siteIds)].sort((left, right) => left.localeCompare(right));
};

export const parseSiteIdsParam = (rawSiteIds: string | null | undefined): string[] => {
    if (!rawSiteIds) {
        return [];
    }

    return normalizeSiteIdList(String(rawSiteIds).split(","));
};

export const parseSelectionFromSearch = (search: string): IBoardSelectionStored | null => {
    const searchParams = new URLSearchParams(search);
    const contextId = String(searchParams.get("id") ?? "").trim();
    if (!contextId) {
        return null;
    }

    const selectionMode = normalizeContextType(searchParams.get("typename"));
    const selectedSiteIds = selectionMode === "region"
        ? parseSiteIdsParam(searchParams.get("siteids"))
        : [];

    return {
        selectionMode,
        contextId,
        ...(selectedSiteIds.length > 0 ? { selectedSiteIds } : {}),
    };
};

export const parseSelectionFromStorage = (rawSelection: string | null): IBoardSelectionStored | null => {
    if (!rawSelection) {
        return null;
    }

    try {
        const parsed = JSON.parse(rawSelection);
        const contextId = String(parsed?.contextId ?? "").trim();
        if (!contextId) {
            return null;
        }

        const selectionMode = parsed?.selectionMode === "region" ? "region" : "site";
        const selectedSiteIds = selectionMode === "region"
            ? normalizeSiteIdList(Array.isArray(parsed?.selectedSiteIds) ? parsed.selectedSiteIds : [])
            : [];

        return {
            selectionMode,
            contextId,
            ...(selectedSiteIds.length > 0 ? { selectedSiteIds } : {}),
        };
    } catch {
        return null;
    }
};

export const serializeSelectionForStorage = (selection: IBoardSelectionInput): string => {
    const contextId = String(selection.contextId ?? "").trim();
    const selectedSiteIds = selection.selectionMode === "region"
        ? normalizeSiteIdList(selection.selectedSiteIds ?? [])
        : [];

    return JSON.stringify({
        selectionMode: selection.selectionMode,
        contextId,
        ...(selectedSiteIds.length > 0 ? { selectedSiteIds } : {}),
    });
};

export const buildContextKey = (contextType: TBoardContextType, contextId: string): string => {
    return `${contextType}:${String(contextId ?? "").trim()}`;
};

export const selectionCoversAllSites = (selectedSiteIds: string[], allSiteIds: string[]): boolean => {
    const normalizedSelected = sortSiteIds(selectedSiteIds);
    const normalizedAll = sortSiteIds(allSiteIds);
    if (normalizedAll.length < 1) {
        return false;
    }

    return normalizedSelected.length === normalizedAll.length
        && normalizedSelected.every((siteId, index) => siteId === normalizedAll[index]);
};

export const buildSelectionKey = (
    selectionMode: TBoardContextType,
    contextId: string,
    selectedSiteIds: string[],
    allSiteIds: string[],
): string => {
    const normalizedContextId = String(contextId ?? "").trim();
    if (!normalizedContextId) {
        return "";
    }

    if (selectionMode === "site") {
        return buildContextKey("site", normalizedContextId);
    }

    if (selectionCoversAllSites(selectedSiteIds, allSiteIds)) {
        return `${buildContextKey("region", normalizedContextId)}:all`;
    }

    return `${buildContextKey("region", normalizedContextId)}:sites:${sortSiteIds(selectedSiteIds).join(",")}`;
};

export const buildDraftContextId = (
    selectionMode: TBoardContextType,
    contextId: string,
    selectedSiteIds: string[],
    allSiteIds: string[],
): string => {
    const normalizedContextId = String(contextId ?? "").trim();
    if (!normalizedContextId) {
        return "";
    }

    if (selectionMode === "site" || selectionCoversAllSites(selectedSiteIds, allSiteIds)) {
        return normalizedContextId;
    }

    return `${normalizedContextId}__${sortSiteIds(selectedSiteIds).join(",")}`;
};

export const buildSelectionQueryString = (
    currentSearch: string,
    selectionMode: TBoardContextType,
    contextId: string,
    selectedSiteIds: string[],
    allSiteIds: string[],
): string => {
    const searchParams = new URLSearchParams(currentSearch);
    searchParams.set("typename", selectionMode === "region" ? REGION_TYPENAME : SITE_TYPENAME);
    searchParams.set("id", String(contextId ?? "").trim());

    if (selectionMode === "region" && !selectionCoversAllSites(selectedSiteIds, allSiteIds)) {
        searchParams.set("siteids", sortSiteIds(selectedSiteIds).join(","));
    } else {
        searchParams.delete("siteids");
    }

    return searchParams.toString();
};
